// @ts-ignore: no type decl for this library
import * as jedLib from "jed";

// Keep translations usable before an application installs its catalog.  This
// matters for shared components that can render while providers are still
// waiting for configuration data.
export let jed: any = new jedLib.Jed({});

let activeLanguage = "en";

export interface PogenCatalogueMetadata {
  total: number;
  translated: number;
  untranslated: string[];
  optional: string[];
}

export interface TranslationCatalogue {
  domain?: string;
  lang?: string;
  completeness?: number;
  plural_forms?: string;
  locale_data?: {
    messages?: Record<string, unknown>;
  };
  _pogen?: PogenCatalogueMetadata;
}

export type TranslationCatalogues = Record<string, TranslationCatalogue>;

function normalizeLanguageTag(language: string): string {
  return language.trim().replaceAll("_", "-").toLowerCase();
}

/** Return locale candidates from the broadest to the most specific. */
export function languageFallbackChain(language: string): string[] {
  const normalized = normalizeLanguageTag(language);
  if (!normalized) return [];
  const parts = normalized.split("-").filter(Boolean);
  return parts.map((_, index) => parts.slice(0, index + 1).join("-"));
}

function catalogueMessages(
  catalogue: TranslationCatalogue | undefined,
): Record<string, unknown> {
  return catalogue?.locale_data?.messages ?? {};
}

function usableMessage(value: unknown): boolean {
  if (!Array.isArray(value) || value.length === 0) return false;
  if (value.length === 1) return typeof value[0] === "string" && !!value[0];
  return value.slice(1).every((entry) => typeof entry === "string" && !!entry);
}

interface CatalogueState {
  catalogue: TranslationCatalogue;
  owned: Set<string>;
  untranslated: Set<string>;
  optional: Set<string>;
}

function catalogueState(catalogue: TranslationCatalogue): CatalogueState {
  const messages = catalogueMessages(catalogue);
  const optional = new Set(catalogue._pogen?.optional ?? []);
  const untranslated = new Set(catalogue._pogen?.untranslated ?? []);
  const owned = new Set([
    ...Object.keys(messages).filter((key) => key !== ""),
    ...untranslated,
    ...optional,
  ]);
  if (!catalogue._pogen) {
    for (const key of owned) {
      if (!usableMessage(messages[key])) untranslated.add(key);
    }
  }
  return { catalogue, owned, untranslated, optional };
}

function finishCatalogue(
  language: string,
  catalogue: TranslationCatalogue,
  owned: Set<string>,
  untranslated: Set<string>,
  optional: Set<string>,
): TranslationCatalogue {
  const total = [...owned].filter((key) => !optional.has(key)).length;
  const translated = [...owned].filter(
    (key) => !optional.has(key) && !untranslated.has(key),
  ).length;
  const completeness =
    language.split("-", 1)[0] === "en"
      ? 100
      : total === 0
        ? 0
        : Math.floor((translated * 100) / total);
  return {
    ...catalogue,
    completeness,
    _pogen: {
      total,
      translated,
      untranslated: [...untranslated].sort(),
      optional: [...optional].sort(),
    },
  };
}

/**
 * Merge catalogue sources from lowest to highest priority.
 *
 * A higher-priority source owns even its untranslated entries. This lets an
 * application deliberately override a dependency without inheriting wording
 * from that dependency. Locale fallback uses different semantics below: an
 * untranslated regional entry inherits its parent locale.
 */
function mergeCatalogueSources(
  language: string,
  lower: TranslationCatalogue | undefined,
  higher: TranslationCatalogue,
): TranslationCatalogue {
  if (!lower) {
    const state = catalogueState(higher);
    return finishCatalogue(
      language,
      higher,
      state.owned,
      state.untranslated,
      state.optional,
    );
  }
  const low = catalogueState(lower);
  const high = catalogueState(higher);
  const messages = { ...catalogueMessages(lower) };
  const untranslated = new Set(low.untranslated);
  const optional = new Set(low.optional);
  const owned = new Set(low.owned);
  const highMessages = catalogueMessages(higher);
  for (const key of high.owned) {
    owned.add(key);
    untranslated.delete(key);
    optional.delete(key);
    if (Object.hasOwn(highMessages, key)) messages[key] = highMessages[key];
    else delete messages[key];
    if (high.untranslated.has(key)) untranslated.add(key);
    if (high.optional.has(key)) optional.add(key);
  }
  const highHeader = highMessages[""];
  if (highHeader !== undefined) messages[""] = highHeader;
  return finishCatalogue(
    language,
    {
      ...lower,
      ...higher,
      locale_data: { messages },
    },
    owned,
    untranslated,
    optional,
  );
}

function overlayLocaleFallback(
  language: string,
  parent: TranslationCatalogue | undefined,
  regional: TranslationCatalogue,
): TranslationCatalogue {
  if (!parent) return mergeCatalogueSources(language, undefined, regional);
  const low = catalogueState(parent);
  const high = catalogueState(regional);
  const messages = { ...catalogueMessages(parent) };
  const untranslated = new Set(low.untranslated);
  const optional = new Set(low.optional);
  const owned = new Set(low.owned);
  const highMessages = catalogueMessages(regional);
  for (const key of high.owned) {
    owned.add(key);
    const hasUsableRegionalTranslation =
      !high.untranslated.has(key) && usableMessage(highMessages[key]);
    if (hasUsableRegionalTranslation) {
      messages[key] = highMessages[key];
      untranslated.delete(key);
      optional.delete(key);
      if (high.optional.has(key)) optional.add(key);
    } else if (!low.owned.has(key)) {
      delete messages[key];
      untranslated.add(key);
      if (high.optional.has(key)) optional.add(key);
    }
  }
  const highHeader = highMessages[""];
  if (highHeader !== undefined) messages[""] = highHeader;
  return finishCatalogue(
    language,
    {
      ...parent,
      ...regional,
      locale_data: { messages },
    },
    owned,
    untranslated,
    optional,
  );
}

/** Compose dependency catalogues followed by the application catalogue. */
export function composeTranslationCatalogues(
  ...sources: TranslationCatalogues[]
): TranslationCatalogues {
  const byLanguage = new Map<string, TranslationCatalogue>();
  const outputKeys = new Map<string, string>();
  for (const source of sources) {
    for (const [language, catalogue] of Object.entries(source)) {
      const normalized = normalizeLanguageTag(language);
      if (!normalized) continue;
      outputKeys.set(normalized, language.replaceAll("_", "-"));
      byLanguage.set(
        normalized,
        mergeCatalogueSources(
          normalized,
          byLanguage.get(normalized),
          catalogue,
        ),
      );
    }
  }

  const withFallback = new Map<string, TranslationCatalogue>();
  for (const language of byLanguage.keys()) {
    let catalogue: TranslationCatalogue | undefined;
    for (const candidate of languageFallbackChain(language)) {
      const next = byLanguage.get(candidate);
      if (next) catalogue = overlayLocaleFallback(language, catalogue, next);
    }
    if (catalogue) withFallback.set(language, catalogue);
  }

  return Object.fromEntries(
    [...withFallback].map(([language, catalogue]) => [
      outputKeys.get(language) ?? language,
      catalogue,
    ]),
  );
}

function resolveTranslationCatalogue(
  language: string,
  catalogues: TranslationCatalogues,
): TranslationCatalogue | undefined {
  const normalizedCatalogues = new Map(
    Object.entries(catalogues).map(([key, catalogue]) => [
      normalizeLanguageTag(key),
      catalogue,
    ]),
  );
  let result: TranslationCatalogue | undefined;
  for (const candidate of languageFallbackChain(language)) {
    const catalogue = normalizedCatalogues.get(candidate);
    if (catalogue) result = overlayLocaleFallback(language, result, catalogue);
  }
  return result;
}

/**
 * Set up jed library for internationalization,
 * based on browser language settings.
 */
export function setupI18n(
  lang: string,
  strings: Record<string, unknown>,
): void {
  lang = normalizeLanguageTag(lang);
  activeLanguage = lang;
  const catalog =
    resolveTranslationCatalogue(lang, strings as TranslationCatalogues) ?? {};
  jed = new jedLib.Jed(catalog);
}

/**
 * Use different translations for testing.  Should not be used outside
 * of test cases.
 */
export function internalSetStrings(langStrings: any): void {
  jed = new jedLib.Jed(langStrings);
}

declare const __translated: unique symbol;
export type TranslatedString = string & { [__translated]: true };

/**
 * Convert template strings to a msgid
 */
function toI18nString(stringSeq: ReadonlyArray<string>): TranslatedString {
  let s = "";
  for (let i = 0; i < stringSeq.length; i++) {
    s += stringSeq[i];
    if (i < stringSeq.length - 1) {
      s += `%${i + 1}$s`;
    }
  }
  return s as TranslatedString;
}

export type FixedTranslations = Readonly<
  { en: string } & Record<string, string>
>;

function substituteSerializedValues(
  translation: string,
  values: readonly any[],
): TranslatedString {
  return translation.replace(/%(\d+)\$s/g, (_match, index: string) =>
    String(values[Number.parseInt(index, 10) - 1]),
  ) as TranslatedString;
}

function fixedTranslation(
  translations: FixedTranslations,
  allowCatalogFallback: boolean,
  values: readonly any[],
): TranslatedString {
  const normalizedTranslations = new Map(
    Object.entries(translations).map(([language, translation]) => [
      normalizeLanguageTag(language),
      translation,
    ]),
  );
  const source = translations.en;
  const fallbackChain = languageFallbackChain(activeLanguage);
  const baseLanguage = fallbackChain[0] ?? "en";

  // The source language is itself fixed and must never be overridden by an
  // English catalogue entry.
  if (baseLanguage === "en") {
    return substituteSerializedValues(source, values);
  }

  const prescribed = fallbackChain
    .toReversed()
    .map((language) => normalizedTranslations.get(language))
    .find((translation) => translation !== undefined);
  if (prescribed !== undefined) {
    return substituteSerializedValues(prescribed, values);
  }

  if (allowCatalogFallback) {
    return translateSerialized(source, values);
  }
  return substituteSerializedValues(source, values);
}

/**
 * Translate prescribed text whose authoritative translations live in source.
 * Languages without a fixed translation continue through the gettext
 * catalogue and ultimately fall back to the prescribed English source.
 */
export function fixed(
  translations: FixedTranslations,
  ...values: any[]
): TranslatedString {
  return fixedTranslation(translations, true, values);
}

/**
 * Translate prescribed text using only source-controlled translations.
 */
export function fixedOnly(
  translations: FixedTranslations,
  ...values: any[]
): TranslatedString {
  return fixedTranslation(translations, false, values);
}

function translateSerialized(
  source: string,
  values: readonly any[],
): TranslatedString {
  // jed throws an Error when the key is empty.
  if (!source) return "" as TranslatedString;
  return jed
    .translate(source)
    .ifPlural(1, source)
    .fetch(...values);
}

/**
 * Internationalize a string template with arbitrary serialized values.
 */
export function singular(
  stringSeq: TemplateStringsArray,
  ...values: any[]
): TranslatedString {
  const s = toI18nString(stringSeq);
  return translateSerialized(s, values);
}

function withContext(ctx: string): typeof singular {
  return function (t: TemplateStringsArray, ...v: any[]): TranslatedString {
    const s = toI18nString(t);
    const tr = jed
      .translate(s)
      .withContext(ctx)
      .ifPlural(1, s)
      .fetch(...v);
    return tr;
  };
}

/**
 * Internationalize a string template without serializing
 */
export function translate(
  stringSeq: TemplateStringsArray,
  ...values: any[]
): TranslatedString[] {
  const s = toI18nString(stringSeq);
  if (!s) return [];
  const translation: TranslatedString = jed.ngettext(s, s, 1);
  return replacePlaceholderWithValues(translation, values);
}

/**
 * Internationalize a string template without serializing
 */
export function Translate({
  children,
  debug,
  context: ctx,
}: {
  children: any;
  debug?: boolean;
  context?: string;
}): any {
  const c = [].concat(children);
  const s = stringifyArray(c);
  if (!s) return [];
  const translation: TranslatedString = ctx
    ? jed.npgettext(ctx, s, s, 1)
    : jed.ngettext(s, s, 1);
  if (debug) {
    console.log("looking for ", s, "got", translation);
  }
  return replacePlaceholderWithValues(translation, c);
}

function replacePlaceholderWithValues(
  translation: TranslatedString,
  childArray: Array<any>,
): Array<any> {
  const tr = translation.split(/%(\d+)\$s/);
  // const childArray = toChildArray(children);
  // Merge consecutive string children.
  const placeholderChildren = [];
  for (let i = 0; i < childArray.length; i++) {
    const x = childArray[i];
    if (x === undefined) {
      continue;
    } else if (typeof x === "string") {
      continue;
    } else {
      placeholderChildren.push(x);
    }
  }
  const result = [];
  for (let i = 0; i < tr.length; i++) {
    if (i % 2 == 0) {
      // Text
      result.push(tr[i]);
    } else {
      const childIdx = Number.parseInt(tr[i]) - 1;
      result.push(placeholderChildren[childIdx]);
    }
  }
  return result;
}

function stringifyArray(children: Array<any>): string {
  let n = 1;
  const ss = children.map((c) => {
    if (typeof c === "string") {
      return c;
    }
    return `%${n++}$s`;
  });
  const s = ss.join("").replace(/ +/g, " ").trim();
  return s;
}

export type InternationalizationAPI = typeof i18n;
export type Translator = (i18n: InternationalizationAPI) => TranslatedString;

export const i18n = {
  str: singular,
  fixed,
  fixedOnly,
  ctx: withContext,
  singular,
  Translate,
  translate,
};
